import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import Input from '../../common/Input/Input';
import Button from '../../common/Button/Button';
import Card from '../../common/Card/Card';
import Notification from '../../common/Notification/Notification';
import { useAuth } from '../../../hooks/useAuth';
import { RegisterCredentials } from '../../../types/auth.types';
import { resendConfirmation } from '../../../api/auth';
import { useResendTimer } from '../../../hooks/useResendTimer';
import GoogleSignInButton from '../GoogleSignInButton/GoogleSignInButton';
import { authErrorToReactNode } from '../authErrorDisplay';

const RegisterForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState<RegisterCredentials>({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  
  const [trialToken, setTrialToken] = useState<string | null>(null);

  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) {
      const decodedEmail = decodeURIComponent(emailParam);
      setCredentials((prev) => ({ ...prev, email: decodedEmail }));
    }
    const trialParam = searchParams.get('trial_token');
    if (trialParam) {
      setTrialToken(trialParam);
    }
  }, [searchParams]);
  
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  
  const { register, loading, error, clearError } = useAuth();
  const { isDisabled, startTimer, buttonText } = useResendTimer(credentials.email);
  
  const handleGoogleLoginSuccess = () => {
    navigate('/start-trial');
  };
  
  const handleGoogleLoginFailure = (error: Error) => {
    console.error('Google sign-in failed:', error);
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials((prev: RegisterCredentials) => ({ ...prev, [name]: value }));
  };
  
  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCredentials((prev) => ({ ...prev, confirmPassword: e.target.value }));
  };
  
  const validateForm = (): boolean => {
    if (credentials.password !== credentials.confirmPassword) {
      setPasswordError("Passwords don't match");
      return false;
    }
    
    if (credentials.password.length < 8) {
      setPasswordError("Password must be at least 8 characters");
      return false;
    }
    
    setPasswordError(null);
    return true;
  };
  
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    // Clear any previous errors before registration
    clearError();
    setPasswordError(null);
    
    const payload = {
      username: credentials.username,
      email: credentials.email,
      password: credentials.password,
      ...(trialToken ? { trial_token: trialToken } : {}),
    };
    const result = await register(payload);
    if (result.success) {
      setRegistrationSuccess(true);
    } else if (result.error) {
      if (typeof result.error === 'string') {
        setPasswordError(result.error);
      }
    }
  };

  const handleResendConfirmation = async () => {
    setResendLoading(true);
    setResendMessage(null);
    try {
      await resendConfirmation(credentials.email);
      setResendMessage('Confirmation email sent successfully!');
      startTimer(); // Start 60-second countdown
    } catch (err: any) {
      setResendMessage(err.response?.data?.detail || err.message || 'Failed to send confirmation email');
    } finally {
      setResendLoading(false);
    }
  };
  
  return (
    <Card className="max-w-md mx-auto">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Create an Account</h2>
        <p className="text-gray-600 mt-1">
          {trialToken
            ? 'Sign up to start your 5-day Growth trial (no card required)'
            : 'Sign up to start using WebLeads'}
        </p>
      </div>
      
      {registrationSuccess ? (
        <div className="space-y-4">
          <Notification
            type="success"
            message="Please check your email to confirm your account. Make sure to check your spam folder if you don't see it in your inbox."
            onClose={() => setRegistrationSuccess(false)}
          />
          <div className="text-center space-y-4">
            <p className="text-gray-600">
              We've sent a confirmation email to <strong>{credentials.email}</strong>
            </p>
            <div className="space-y-2">
              <Button
                onClick={handleResendConfirmation}
                isLoading={resendLoading}
                disabled={isDisabled || resendLoading}
                fullWidth
              >
                {buttonText}
              </Button>
              {resendMessage && (
                <Notification
                  type={resendMessage.includes('successfully') ? 'success' : 'error'}
                  message={resendMessage}
                  onClose={() => setResendMessage(null)}
                />
              )}
            </div>
            <div className="text-sm">
              <Link to="/login" className="font-medium text-primary hover:text-primary/80">
                Go to Login
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <>
          {(error || passwordError) && (
            <Notification
              type="error"
              message={passwordError || authErrorToReactNode(error)}
              onClose={() => {
                clearError();
                setPasswordError(null);
              }}
            />
          )}
          
          <GoogleSignInButton
            onSuccess={handleGoogleLoginSuccess}
            onFailure={handleGoogleLoginFailure}
            trialToken={trialToken}
          />
          
          <p className="text-xs text-gray-500 text-center mt-2">
            By signing in with Google, you agree to our{' '}
            <a 
              href="https://www.webleads.site/terms" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Terms of Service
            </a>
            {' '}and{' '}
            <a 
              href="https://www.webleads.site/privacy" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Privacy Policy
            </a>
          </p>
          
          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">or</span>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          id="username"
          name="username"
          type="text"
          label="Username"
          value={credentials.username}
          onChange={handleChange}
          required
          autoComplete="username"
        />
        
        <Input
          id="email"
          name="email"
          type="email"
          label="Email"
          value={credentials.email}
          onChange={handleChange}
          required
          autoComplete="email"
        />
        
        <Input
          id="password"
          name="password"
          type="password"
          label="Password"
          value={credentials.password}
          onChange={handleChange}
          required
          autoComplete="new-password"
        />
        
        <Input
          id="confirmPassword"
          name="confirmPassword"
          type="password"
          label="Confirm Password"
          value={credentials.confirmPassword}
          onChange={handleConfirmPasswordChange}
          required
          autoComplete="new-password"
        />
        
        <div className="flex items-center justify-between">
          <div className="text-sm">
            <Link to="/login" className="font-medium text-primary hover:text-primary/80">
              Already have an account? Sign in
            </Link>
          </div>
        </div>
        
        <p className="text-xs text-gray-500 text-center">
          By registering, you agree to our{' '}
          <a 
            href="https://www.webleads.site/terms" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            Terms of Service
          </a>
          {' '}and{' '}
          <a 
            href="https://www.webleads.site/privacy" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            Privacy Policy
          </a>
        </p>
        
            <Button type="submit" fullWidth isLoading={loading}>
              Register
            </Button>
          </form>
        </>
      )}
    </Card>
  );
};

export default RegisterForm; 